// src/components/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Users,
  ClipboardList,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import AdminService from '../../services/AdminService';
// import AdminServiceDebug from '../../services/AdminServiceDebug';

const AdminDashboard = ({ isMobile }) => {
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalUsers: 0,
    totalRevenue: 0,
    pendingBookings: 0,
    todayBookings: 0,
    thisWeekBookings: 0,
    recentBookings: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Debug: Check what's actually in Firebase (temporarily disabled)
        console.log('🔍 Starting dashboard data load...');
        // await AdminServiceDebug.debugFirebaseData();
        // await AdminServiceDebug.testAdminService();

        // Get comprehensive dashboard statistics with real data
        const dashboardStats = await AdminService.getDashboardStatistics();

        setStats({
          totalBookings: dashboardStats.bookings.total,
          totalUsers: dashboardStats.users.total,
          totalRevenue: dashboardStats.revenue.total,
          pendingBookings: dashboardStats.bookings.pending,
          todayBookings: dashboardStats.bookings.today,
          thisWeekBookings: dashboardStats.bookings.thisWeek,
          thisMonthBookings: dashboardStats.bookings.thisMonth,
          thisMonthRevenue: dashboardStats.revenue.thisMonth,
          averageBookingValue: dashboardStats.revenue.average,
          adminUsers: dashboardStats.users.admins,
          completedBookings: dashboardStats.bookings.completed,
          cancelledBookings: dashboardStats.bookings.cancelled,
          recentBookings: dashboardStats.recentBookings.slice(0, 5),
          stripeMetrics: dashboardStats.revenue.stripe,
          lastUpdated: dashboardStats.lastUpdated
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();

    // Refresh data every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-24 rounded-lg"></div>
            ))}
          </div>
          <div className="bg-gray-200 h-64 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Dashboard Overview</h2>
        <p className="text-gray-600">Monitor your business performance and recent activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Bookings</p>
              <p className="text-2xl font-bold">{stats.totalBookings || 0}</p>
              <p className="text-blue-200 text-xs mt-1">
                {stats.completedBookings || 0} completed • {stats.pendingBookings || 0} pending
              </p>
            </div>
            <ClipboardList className="h-8 w-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue || 0)}</p>
              <p className="text-green-200 text-xs mt-1">
                {formatCurrency(stats.thisMonthRevenue || 0)} this month
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Users</p>
              <p className="text-2xl font-bold">{stats.totalUsers || 0}</p>
              <p className="text-purple-200 text-xs mt-1">
                {stats.adminUsers || 0} admin • {(stats.totalUsers || 0) - (stats.adminUsers || 0)} regular
              </p>
            </div>
            <Users className="h-8 w-8 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Average Value</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.averageBookingValue || 0)}</p>
              <p className="text-orange-200 text-xs mt-1">
                per booking
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Enhanced Stripe Revenue Analytics */}
      {stats.stripeMetrics && (stats.stripeMetrics.lastThirtyDays?.gross > 0 || stats.stripeMetrics.verifiedRevenue > 0) && (
        <div className="bg-white border rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">💳 Stripe Revenue Analytics (Last 30 Days)</h3>
            <span className="text-sm text-gray-500">Real-time Stripe data</span>
          </div>

          {/* Revenue Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-green-600 text-sm font-medium">Gross Revenue</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(stats.stripeMetrics.lastThirtyDays?.gross || 0)}</p>
              <p className="text-xs text-green-600 mt-1">Before refunds & fees</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-600 text-sm font-medium">Net Revenue</p>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(stats.stripeMetrics.lastThirtyDays?.net || 0)}</p>
              <p className="text-xs text-blue-600 mt-1">After refunds</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-red-600 text-sm font-medium">Total Refunds</p>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(stats.stripeMetrics.lastThirtyDays?.refunded || 0)}</p>
              <p className="text-xs text-red-600 mt-1">{stats.stripeMetrics.refunds?.count || 0} refunds</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-orange-600 text-sm font-medium">Verified Revenue</p>
              <p className="text-2xl font-bold text-orange-900">{formatCurrency(stats.stripeMetrics.verifiedRevenue || 0)}</p>
              <p className="text-xs text-orange-600 mt-1">From actual bookings</p>
            </div>
          </div>

          {/* Transaction Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-purple-600 text-sm font-medium">Total Transactions</p>
              <p className="text-xl font-bold text-purple-900">{stats.stripeMetrics.transactions?.total || 0}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-green-600 text-sm font-medium">Success Rate</p>
              <p className="text-xl font-bold text-green-900">
                {stats.stripeMetrics.transactions?.total > 0
                  ? (((stats.stripeMetrics.transactions?.successful || 0) / (stats.stripeMetrics.transactions?.total || 1)) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-yellow-600 text-sm font-medium">Avg. Transaction</p>
              <p className="text-xl font-bold text-yellow-900">{formatCurrency(stats.stripeMetrics.averages?.transactionAmount || 0)}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-red-600 text-sm font-medium">Avg. Refund</p>
              <p className="text-xl font-bold text-red-900">{formatCurrency(stats.stripeMetrics.averages?.refundAmount || 0)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Refunds Section */}
      {stats.stripeMetrics?.refunds && stats.stripeMetrics.refunds.count > 0 && (
        <div className="bg-white border rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">🔄 Refund Analytics</h3>
            <span className="text-sm text-gray-500">Last 30 days</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-red-600 text-sm font-medium">Total Refunded</p>
              <p className="text-xl font-bold text-red-900">{formatCurrency(stats.stripeMetrics.refunds?.totalAmount || 0)}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-yellow-600 text-sm font-medium">Refund Count</p>
              <p className="text-xl font-bold text-yellow-900">{stats.stripeMetrics.refunds?.count || 0}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-orange-600 text-sm font-medium">Pending</p>
              <p className="text-xl font-bold text-orange-900">{stats.stripeMetrics.refunds?.pending || 0}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600 text-sm font-medium">Refund Rate</p>
              <p className="text-xl font-bold text-gray-900">
                {stats.stripeMetrics.transactions?.total > 0
                  ? (((stats.stripeMetrics.refunds?.count || 0) / (stats.stripeMetrics.transactions?.total || 1)) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📅 Activity Timeline</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Today</span>
              <span className="font-semibold">{stats.todayBookings || 0} bookings</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">This Week</span>
              <span className="font-semibold">{stats.thisWeekBookings || 0} bookings</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">This Month</span>
              <span className="font-semibold">{stats.thisMonthBookings || 0} bookings</span>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📊 Booking Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-yellow-600">Pending</span>
              <span className="font-semibold">{stats.pendingBookings || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-blue-600">Completed</span>
              <span className="font-semibold">{stats.completedBookings || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-red-600">Cancelled</span>
              <span className="font-semibold">{stats.cancelledBookings || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">⚡ Quick Actions</h3>
          <div className="space-y-2">
            <button className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
              📋 View All Bookings
            </button>
            <button className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
              👥 Manage Users
            </button>
            <button className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
              📅 Calendar View
            </button>
            <button className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 text-sm text-gray-700">
              🔍 Stripe Audit
            </button>
          </div>
          {stats.lastUpdated && (
            <div className="mt-4 pt-3 border-t text-xs text-gray-500">
              Last updated: {new Date(stats.lastUpdated).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* User Analytics */}
      {stats.totalUsers > 0 && (
        <div className="bg-white border rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">👥 User Analytics</h3>
            <span className="text-sm text-gray-500">User-Stripe connections</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-600 text-sm font-medium">Total Users</p>
              <p className="text-2xl font-bold text-blue-900">{stats.totalUsers || 0}</p>
              <p className="text-xs text-blue-600 mt-1">{stats.adminUsers || 0} admins</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-purple-600 text-sm font-medium">Stripe Customers</p>
              <p className="text-2xl font-bold text-purple-900">
                {stats.stripeMetrics?.verifiedRevenue ?
                  Math.round((stats.stripeMetrics.transactions?.successful || 0) / Math.max(stats.totalUsers, 1) * 100) : 0}%
              </p>
              <p className="text-xs text-purple-600 mt-1">have made payments</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-green-600 text-sm font-medium">Customer Value</p>
              <p className="text-2xl font-bold text-green-900">
                {formatCurrency(stats.stripeMetrics?.averages?.transactionAmount || 0)}
              </p>
              <p className="text-xs text-green-600 mt-1">avg per transaction</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Bookings */}
      <div className="bg-white border rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Bookings</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {stats.recentBookings.length > 0 ? (
            stats.recentBookings.map((booking) => (
              <div key={booking.bookingId} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {booking.status || 'Pending'}
                      </span>
                      <span className="font-medium">{booking.service?.title}</span>
                      <span className="text-sm text-gray-500">
                        {booking.bookingType === 'guest' ? '(Guest)' : '(User)'}
                      </span>
                      {booking.stripePayment && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          💳 Stripe
                        </span>
                      )}
                      {booking.payment?.hasRefunds && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          🔄 Refunded
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {booking.userName} • {formatDate(booking.schedule?.date)} • {formatCurrency(booking.payment?.total || booking.service?.price || 0)}
                      {booking.stripePayment && (
                        <span className="ml-2 text-xs text-green-600">
                          (Stripe: {booking.stripePayment.status})
                        </span>
                      )}
                      {booking.payment?.hasRefunds && (
                        <span className="ml-2 text-xs text-red-600">
                          (Refunded: {formatCurrency(booking.payment.stripeRefunded || 0)})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDate(booking.createdAt)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              No recent bookings found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;